import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
const LABOR_TIME_ZONE = "America/Chicago";

interface AdminLaborSession {
  source: "device" | "task";
  id: number;
  userId: string;
  page: string;
  startedAt: string;
  endedAt: string | null;
  deviceCount: number | null;
  active: boolean;
}

interface AdminLaborEmployee {
  key: string;
  label: string;
  userId: string;
  sessions: AdminLaborSession[];
}

interface AdminLaborRecentSessionsResponse {
  employees: AdminLaborEmployee[];
}

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  timeZone: LABOR_TIME_ZONE,
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function formatLaborTimestamp(value: string | null): string {
  if (value == null || value.trim() === "") {
    return "—";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return dateTimeFormatter.format(parsed);
}

function formatStationPage(page: string): string {
  const trimmed = page.trim();
  if (trimmed === "cal+test") {
    return "Cal + test";
  }
  if (trimmed === "Orders") {
    return "Orders (misc)";
  }
  if (trimmed === "") {
    return "—";
  }
  return trimmed
    .split("-")
    .map((part) => (part.length === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(" ");
}

function formatDeviceCount(session: AdminLaborSession): string {
  if (session.deviceCount == null) {
    return "—";
  }
  return session.deviceCount.toLocaleString();
}

type LaborSortKey = "page" | "deviceCount" | "startedAt" | "endedAt";

const LABOR_SESSION_COLUMNS: HubDataTableColumn<AdminLaborSession, LaborSortKey>[] = [
  {
    id: "page",
    header: "Station",
    sortKey: "page",
    sortValue: (row) => row.page,
    cell: (row) => formatStationPage(row.page),
  },
  {
    id: "deviceCount",
    header: "Devices",
    sortKey: "deviceCount",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.deviceCount ?? -1,
    cell: (row) => formatDeviceCount(row),
  },
  {
    id: "startedAt",
    header: "Start",
    sortKey: "startedAt",
    overflow: "nowrap",
    sortValue: (row) => row.startedAt,
    cell: (row) => formatLaborTimestamp(row.startedAt),
  },
  {
    id: "endedAt",
    header: "Stop",
    sortKey: "endedAt",
    overflow: "nowrap",
    sortValue: (row) => (row.active ? "" : row.endedAt ?? ""),
    cell: (row) => (row.active ? "—" : formatLaborTimestamp(row.endedAt)),
  },
];

function EmployeeLaborSection({ employee }: { employee: AdminLaborEmployee }) {
  const activeSession = employee.sessions.find((session) => session.active) ?? null;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>{employee.label}</CardTitle>
        {activeSession ? (
          <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-emerald-200">
            Active on {formatStationPage(activeSession.page)}
          </span>
        ) : null}
      </CardHeader>
      <CardBody className="overflow-x-auto p-0">
        <HubDataTable
          columns={LABOR_SESSION_COLUMNS}
          rows={employee.sessions}
          rowKey={(session) => `${session.source}:${session.id}`}
          fillViewport={false}
          empty="No labor sessions recorded yet."
          rowClassName={(session) => (session.active ? "bg-emerald-50/40" : undefined)}
          className="border-0 shadow-none"
        />
      </CardBody>
    </Card>
  );
}

export function AdminLaborPage() {
  const laborQuery = useQuery<AdminLaborRecentSessionsResponse, ApiError>({
    queryKey: ["user-admin", "labor-recent-sessions"],
    queryFn: () => apiFetch<AdminLaborRecentSessionsResponse>("/api/user-admin/labor-recent-sessions"),
    refetchInterval: 10_000,
  });

  const employees = laborQuery.data?.employees ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          hoverHint="Recent labor sessions for the configured shop-floor operators. Each section shows the four most recent clocked sessions across hub device labor and the legacy mobile app (Receiving, Transfers, Orders), including any active session (start without stop). Device counts apply to hub sessions only. Refreshes automatically every 10 seconds."
        >
          Labor
        </PageTitleWithHelp>
        <Button
          size="sm"
          variant="secondary"
          type="button"
          onClick={() => void laborQuery.refetch()}
          disabled={laborQuery.isFetching}
        >
          {laborQuery.isFetching ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          )}
          Refresh
        </Button>
      </div>

      {laborQuery.isLoading ? <p className="text-sm text-slate-600">Loading labor sessions…</p> : null}

      {laborQuery.isError ? (
        <p className="text-sm text-red-700">
          {laborQuery.error instanceof ApiError
            ? laborQuery.error.message
            : "Could not load labor sessions."}
        </p>
      ) : null}

      {!laborQuery.isLoading && !laborQuery.isError ? (
        <div className="grid gap-4 xl:grid-cols-2">
          {employees.map((employee) => (
            <EmployeeLaborSection key={employee.key} employee={employee} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
